import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FileText,
  Home,
  Moon,
  Plus,
  Search,
  Settings,
  ShoppingCart,
  Sun,
  Users,
  Wrench,
} from "lucide-react";
import { useThemeStore } from "@/lib/theme-store";
import { cn } from "@/lib/cn";

type Command = {
  id: string;
  label: string;
  hint?: string;
  Icon: typeof Home;
  run: () => void;
  /** Extra words to match on that aren't in the label. */
  keywords?: string;
};

/**
 * ⌘K / Ctrl-K command palette.
 *
 * Hub has `c` and `/`; mail has ⌘K. Billing had no keyboard affordances at all,
 * so a heavy user's hands had to leave the keyboard for every navigation. This
 * closes the parity gap with the same shortcut mail already uses, so the muscle
 * memory carries across the suite.
 *
 * Filtering is a plain subsequence-free substring match over label + keywords.
 * With ~12 commands, fuzzy ranking would be more machinery than the problem
 * needs, and exact substring matching is more predictable to type against.
 */
export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [index, setIndex] = useState(0);
  const navigate = useNavigate();
  const mode = useThemeStore((s) => s.mode);
  const setMode = useThemeStore((s) => s.setMode);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const commands = useMemo<Command[]>(
    () => [
      { id: "new-invoice", label: "New invoice", Icon: Plus, hint: "Create", keywords: "add bill create", run: () => navigate("/invoices/new") },
      { id: "home", label: "Home", Icon: Home, keywords: "dashboard overview", run: () => navigate("/") },
      { id: "invoices", label: "Invoices", Icon: FileText, keywords: "bills sales", run: () => navigate("/invoices") },
      { id: "purchases", label: "Purchases", Icon: ShoppingCart, keywords: "expenses vendors bought", run: () => navigate("/purchases") },
      { id: "clients", label: "Clients", Icon: Users, keywords: "customers contacts", run: () => navigate("/clients") },
      { id: "services", label: "Services", Icon: Wrench, keywords: "items catalogue products", run: () => navigate("/services") },
      { id: "settings", label: "Settings", Icon: Settings, keywords: "profile org gstin bank preferences", run: () => navigate("/settings") },
      {
        id: "theme",
        label: mode === "dark" ? "Switch to light theme" : "Switch to dark theme",
        Icon: mode === "dark" ? Sun : Moon,
        keywords: "appearance dark light mode colour color",
        run: () => setMode(mode === "dark" ? "light" : "dark"),
      },
    ],
    [navigate, mode, setMode],
  );

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return commands;
    return commands.filter((c) =>
      `${c.label} ${c.keywords ?? ""}`.toLowerCase().includes(q),
    );
  }, [commands, query]);

  // Global open/close. Registered once; the handler reads state via the setter
  // form so it never needs re-binding.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((v) => !v);
      } else if (e.key === "Escape") {
        setOpen(false);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Reset per opening, and move focus into the field.
  useEffect(() => {
    if (!open) return;
    setQuery("");
    setIndex(0);
    // rAF so the input exists and is focusable before we reach for it.
    const raf = requestAnimationFrame(() => inputRef.current?.focus());
    return () => cancelAnimationFrame(raf);
  }, [open]);

  // Keep the highlighted row in view when arrowing past the fold.
  useEffect(() => {
    listRef.current?.querySelector('[data-active="true"]')?.scrollIntoView({ block: "nearest" });
  }, [index]);

  if (!open) return null;

  const clamp = (n: number) => (results.length === 0 ? 0 : (n + results.length) % results.length);

  function runAt(i: number) {
    const cmd = results[i];
    if (!cmd) return;
    setOpen(false);
    cmd.run();
  }

  return (
    <div
      className="fixed inset-0 z-[200] flex items-start justify-center px-4 pt-[12vh]"
      onMouseDown={() => setOpen(false)}
    >
      <div className="motion-overlay absolute inset-0 bg-black/30 backdrop-blur-[1px]" data-state="open" />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
        data-state="open"
        className="motion-modal-plain relative w-full max-w-lg overflow-hidden rounded-xl border border-[var(--color-border)] bg-surface elev-3"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 border-b border-[var(--color-border)] px-3">
          <Search size={16} className="text-[var(--color-text-subtle)]" aria-hidden />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setIndex(0);
            }}
            onKeyDown={(e) => {
              if (e.key === "ArrowDown") { e.preventDefault(); setIndex((i) => clamp(i + 1)); }
              else if (e.key === "ArrowUp") { e.preventDefault(); setIndex((i) => clamp(i - 1)); }
              else if (e.key === "Enter") { e.preventDefault(); runAt(index); }
            }}
            placeholder="Search commands…"
            aria-label="Search commands"
            aria-activedescendant={results[index] ? `cmd-${results[index].id}` : undefined}
            aria-controls="command-results"
            className="h-12 flex-1 bg-transparent text-[15px] outline-none placeholder:text-[var(--color-text-subtle)]"
          />
          <kbd className="rounded border border-[var(--color-border)] px-1.5 py-0.5 font-mono text-[10px] text-[var(--color-text-subtle)]">
            esc
          </kbd>
        </div>

        <ul
          ref={listRef}
          id="command-results"
          role="listbox"
          aria-label="Commands"
          className="max-h-[52vh] overflow-y-auto p-1.5"
        >
          {results.length === 0 ? (
            <li className="px-3 py-6 text-center text-[13px] text-[var(--color-text-muted)]">
              Nothing matches “{query}”.
            </li>
          ) : (
            results.map((cmd, i) => {
              const active = i === index;
              const Icon = cmd.Icon;
              return (
                <li key={cmd.id} id={`cmd-${cmd.id}`} role="option" aria-selected={active} data-active={active}>
                  <button
                    type="button"
                    // Highlight follows the pointer too, so mouse and keyboard
                    // never disagree about what Enter would run.
                    onMouseMove={() => setIndex(i)}
                    onClick={() => runAt(i)}
                    className={cn(
                      "flex w-full items-center gap-2.5 rounded-lg px-3 py-2.5 text-left text-[14px] transition",
                      active
                        ? "bg-[var(--color-surface-hover)] text-[var(--color-text)]"
                        : "text-[var(--color-text-muted)]",
                    )}
                  >
                    <Icon size={16} aria-hidden className="shrink-0" />
                    <span className="flex-1 truncate">{cmd.label}</span>
                    {cmd.hint ? (
                      <span className="text-[11px] text-[var(--color-text-subtle)]">{cmd.hint}</span>
                    ) : null}
                  </button>
                </li>
              );
            })
          )}
        </ul>
      </div>
    </div>
  );
}
